import { useEffect, useState } from "react";
import { getAdminOverview, AdminOverview as AdminOverviewData } from "../../lib/adminApi";
import { formatBytes, formatUsd } from "../../lib/adminLabels";
import { AdminNav } from "./AdminNav";
import { Card } from "../../ui/Card";
import { Skeleton } from "../../ui/Skeleton";

// timestamps.ts exports formatRelative (m:ss) and formatWallClock, but not an
// h:mm duration formatter. This local helper remains until a shared one exists.
/** Duration in ms → "h:mm" format */
function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const totalMin = Math.floor(totalSec / 60);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return `${hours}:${String(mins).padStart(2, "0")}`;
}

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Card style={{ flex: "1 1 160px", minWidth: 140 }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--ink-3)", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: "var(--ink)" }}>{value}</div>
    </Card>
  );
}

export function AdminOverview() {
  const [data, setData] = useState<AdminOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminOverview()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <AdminNav />
      <h2 style={{ marginTop: 0, marginBottom: 20 }}>Platform overview</h2>

      {error && <p className="error" style={{ marginBottom: 12 }}>{error}</p>}

      {loading ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          {[140, 160, 120, 150, 140].map((w, i) => (
            <Card key={i} style={{ flex: "1 1 160px", minWidth: 140 }}>
              <Skeleton height={12} width={60} style={{ marginBottom: 10 }} />
              <Skeleton height={28} width={w} />
            </Card>
          ))}
        </div>
      ) : data ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <StatCard label="Users" value={String(data.user_count)} />
          <StatCard label="Transcripts" value={String(data.transcript_count)} />
          <StatCard label="Storage" value={formatBytes(data.audio_bytes)} />
          <StatCard label="Total audio" value={formatDuration(data.audio_duration_ms)} />
          <StatCard label="Total spend" value={formatUsd(data.spend_usd)} />
        </div>
      ) : null}
    </>
  );
}
